import React, { useState, useCallback, useMemo } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch, UseFormTrigger } from 'react-hook-form';
import { Tooltip } from './common/tooltip.component';
import Calendar from './calendar.component';
import ServicesTable from './services-table.component';
import PensionHolidaySection from './pension-holiday-section.component';
import { SubTotalSection } from './SubTotalSection';
import { FreelancerCalculationModal } from './FreelancerCalculationModal';
import { useFreelancerCalculationBreakdown } from '../hooks/useFreelancerCalculationBreakdown.hook';
import AutoGenerateItemsModal from './AutoGenerateItemsModal';
import './FreelancerCard.scss';

interface FreelancerCardProps {
  friend: any;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  
  // Form props
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors<any>;
  trigger: UseFormTrigger<any>;
  
  // Configuration
  isRegularEngagement: boolean;
  currency: string;
  isHourlyPrice: boolean;
  isDailyPrice: boolean;
  isWeeklyPrice: boolean;
  isMonthlyPrice: boolean;
  deductionType: string;
  
  // Data
  hoursPerDayType: string;
  workDates: string;
  showDates: string;
  weekOffDays: string[];
  serviceDescription: string;
  serviceDescriptionError: string;
  
  // Dropdowns
  isShowDatesDropdownOpen: boolean;
  isWeekOffDaysDropdownOpen: boolean;
  showDatesDropdownRef: React.RefObject<HTMLDivElement>;
  weekOffDaysDropdownRef: React.RefObject<HTMLDivElement>;
  
  // Calendar
  showCalendar: boolean;
  activeFreelancerForCalendar: string;
  calendarSelectedDatesByFreelancer: { [key: string]: Date[] };
  
  // Table data
  tableRowsByFreelancer: { [key: string]: any[] };
  serviceTableDataByFreelancer: { [key: string]: any };
  deductionsByFreelancer: { [key: string]: any[] };
  pensionDataByFreelancer: { [key: string]: any };
  holidayDataByFreelancer: { [key: string]: any };
  
  // Copied invoice data
  copiedInvoiceData: any;
  removedCopiedWorkingHours: Set<number>;
  
  // Calculation data
  calculatedSubAmount: any;
  isCalculateInvoiceLoading?: boolean;
  
  // Options
  serviceOptions: any[];
  showDatesOptions: { value: string; label: string }[];
  
  // Error states
  showTooltipError: any;
  
  // Handlers
  handleInputChange: (field: string, value: any) => void;
  handleCalendarOpen: (freelancerId: string) => void;
  handleCalendarClose: () => void;
  handleDateSelect: (freelancerId: string, startDate: Date, endDate?: Date, selectedDates?: Date[]) => void;
  onAutoGenerateItems?: (freelancerId: string, modalData: any, selectedDates: Date[]) => void;
  getModalDataByFreelancer?: (freelancerId: string) => any;
  onRemoveFreelancer?: (freelancerId: string) => void;
  setIsShowDatesDropdownOpen: (open: boolean) => void;
  setIsWeekOffDaysDropdownOpen: (open: boolean) => void;
  setWeekOffDays: React.Dispatch<React.SetStateAction<string[]>>;
  setServiceDescription: (value: string) => void;
  setServiceDescriptionError: (error: string) => void;
  handleRowChange: (freelancerId: string, rowId: string, field: string, value: any) => void;
  handleRemoveRow: (freelancerId: string, rowId: string) => void;
  handleAddWorkingHoursSection: (freelancerId: string, rowData?: any) => void;
  setServiceTableDataByFreelancer: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  handleOpenDeductionModal: (freelancerId: string, index?: number) => void;
  setEditingDeductionData: (data: any) => void;
  setCurrentDeductionFreelancerId: (id: string) => void;
  setEditingDeductionIndex: (index: number | null) => void;
  setEditingCopiedWorkingHourIndex: (index: number) => void;
  setShowDeductionModal: (show: boolean) => void;
  setRemovedCopiedWorkingHours: React.Dispatch<React.SetStateAction<Set<number>>>;
  setDeductionsByFreelancer: React.Dispatch<React.SetStateAction<{ [key: string]: any[] }>>;
  setPensionDataByFreelancer: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  setHolidayDataByFreelancer: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  
  // Timesheet data
  timesheetData?: any[];  // NEW - Timesheet data from Redux
  
  // Additional props
  globalData: any;
  preferences: any;
  validateServiceDescriptionsRef: any;
  t: (key: string) => string;
  
  // For calculation breakdown
  selectedFreelancersForInvoice?: any[];
  calculatedAmounts?: any;
  customerState?: any;
  dispatch?: any;
}

export const FreelancerCard: React.FC<FreelancerCardProps> = ({
  friend,
  index,
  isExpanded,
  onToggle,
  register,
  setValue,
  watch,
  errors,
  trigger,
  isRegularEngagement,
  currency,
  isHourlyPrice,
  isDailyPrice,
  isWeeklyPrice,
  isMonthlyPrice,
  deductionType,
  hoursPerDayType,
  workDates,
  showDates,
  weekOffDays,
  serviceDescription,
  serviceDescriptionError,
  isShowDatesDropdownOpen,
  isWeekOffDaysDropdownOpen,
  showDatesDropdownRef,
  weekOffDaysDropdownRef,
  showCalendar,
  activeFreelancerForCalendar,
  calendarSelectedDatesByFreelancer,
  tableRowsByFreelancer,
  serviceTableDataByFreelancer,
  deductionsByFreelancer,
  pensionDataByFreelancer,
  holidayDataByFreelancer,
  copiedInvoiceData,
  removedCopiedWorkingHours,
  calculatedSubAmount,
  isCalculateInvoiceLoading,
  serviceOptions,
  showDatesOptions,
  showTooltipError,
  handleInputChange,
  handleCalendarOpen,
  handleCalendarClose,
  handleDateSelect,
  onAutoGenerateItems,
  getModalDataByFreelancer,
  onRemoveFreelancer,
  setIsShowDatesDropdownOpen,
  setIsWeekOffDaysDropdownOpen,
  setWeekOffDays,
  setServiceDescription,
  setServiceDescriptionError,
  handleRowChange,
  handleRemoveRow,
  handleAddWorkingHoursSection,
  setServiceTableDataByFreelancer,
  handleOpenDeductionModal,
  setEditingDeductionData,
  setCurrentDeductionFreelancerId,
  setEditingDeductionIndex,
  setEditingCopiedWorkingHourIndex,
  setShowDeductionModal,
  setRemovedCopiedWorkingHours,
  setDeductionsByFreelancer,
  setPensionDataByFreelancer,
  setHolidayDataByFreelancer,
  globalData,
  preferences,
  validateServiceDescriptionsRef,
  t,
  timesheetData,
  selectedFreelancersForInvoice,
  calculatedAmounts,
  customerState,
  dispatch,
}) => {
  const friendUserID = friend.userId || friend.id;
  const friendName = friend.name || friend.userName;
  const friendEmail = friend.email;
  
  // Calculation breakdown modal state
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [showAutoGenerateModal, setShowAutoGenerateModal] = useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  
  // Check if button should blink (first freelancer, customer selected, no working hours)
  // Check if customer is selected
  const hasCustomer = useMemo(() => {
    return !!(customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id);
  }, [customerState?.selectedCustomer]);
  
  const shouldButtonBlink = useMemo(() => {
    if (index !== 0) return false; // Only for first freelancer
    const hasWorkingHours = (tableRowsByFreelancer[friendUserID.toString()] || []).length > 0;
    return hasCustomer && !hasWorkingHours;
  }, [index, hasCustomer, tableRowsByFreelancer, friendUserID]);
  
  // Memoize the callback to prevent infinite re-renders in ServicesTable
  const handleServiceTableDataChange = useCallback((data: any) => {
    setServiceTableDataByFreelancer(prev => ({
      ...prev,
      [friendUserID.toString()]: data
    }));
  }, [friendUserID, setServiceTableDataByFreelancer]);
  
  // Initialize calculation breakdown hook
  const {
    calculationData,
    isLoading: isCalculationLoading,
    fetchCalculationBreakdown,
  } = useFreelancerCalculationBreakdown({
    selectedFreelancersForInvoice: selectedFreelancersForInvoice || [],
    serviceTableDataByFreelancer,
    deductionsByFreelancer,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    copiedInvoiceData,
    removedCopiedWorkingHours,
    calculatedAmounts,
    preferences,
    globalData,
    customerState,
    watch,
    dispatch,
    friendUserId: friendUserID,
  });
  
  const handleShowCalculation = () => {
    fetchCalculationBreakdown();
    setShowCalculationModal(true);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  const getDeductionLabel = (type: string) => {
    switch(type) {
      case 'mileage_report': return t('MILEAGE_REPORT') || 'Mileage Report';
      case 'travel_expenses_pdf': return t('TRAVEL_EXPENSES') || 'Travel Expenses';
      case 'expenses_materials_tools_pdf': return t('MATERIALS_AND_TOOLS') || 'Materials & Tools';
      case 'allowance_outside_sweden': return t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden';
      case 'allowance_in_sweden': return t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden';
      default: return type;
    }
  };

  return (
    <div key={friendUserID} className="user-section">
      {/* Collective Freelancer Header - Figma Design */}
       <div className='d-flex'>
      <div 
        className="collective-freelancer-header"
        onClick={onToggle}
       style={{ cursor: 'pointer', padding: '10px 12px' }}
      >
        <div className="user-info-toggle">
          <button
            className="expand-toggle"
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="chevron-circle">
              <circle cx="12" cy="12" r="10" stroke="#317AF7" strokeWidth="1.5" fill="none" />
              <path
                d={isExpanded ? "M8 14l4-4 4 4" : "M8 10l4 4 4-4"}
                stroke="#317AF7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="user-name-bold">{friendName}</span>
          </button>
        </div>
        <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
          <div className="email-badge-light">{friendEmail}</div>
          <div style={{ flex: 1 }}></div>
          {hasCustomer && (
            <Button
               size="sm"
              className="auto-generate-btn-custom"
              onClick={(e) => {
                e.stopPropagation();
                setShowAutoGenerateModal(true);
              }}
              style={{
                display: 'flex',
                padding: '8px 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '6px',
                borderRadius: '25px',
                border: '2px solid #6750B8',
                fontSize: '14px',
                fontWeight: 500,
                color: '#6750B8',
                backgroundColor: 'transparent',
                animation: shouldButtonBlink ? 'professionalBlink 2s ease-in-out infinite' : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              <style>{`
                @keyframes professionalBlink {
                  0%, 100% {
                    border-color: #6750B8;
                    box-shadow: 0 0 0 rgba(103, 80, 184, 0);
                  }
                  50% {
                    border-color: #8B7DD8;
                    box-shadow: 0 0 15px rgba(103, 80, 184, 0.4), 0 0 30px rgba(103, 80, 184, 0.2);
                  }
                }
              `}</style>
             
              {t('AUTO_GENERATE_ITEMS') || 'Auto Generate Items'}
            </Button>
          )}
         </div>

      </div>
      <div className='ml-2'>
       {index !== 0 && onRemoveFreelancer && (
            <button
              className="remove-freelancer-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowRemoveConfirmModal(true);
              }}
              style={{
               display: 'flex',
                 width: '48px',
                height: '64px',
                // padding: '8px 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '6px',
                borderRadius: '20px',
                border: '1px solid #EAEBF0',
                background: '#FFF',
                cursor: 'pointer'
              }}
            >
               <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M12 12L4 4.00001" stroke="#252525" strokeLinecap="round" />
              </svg>
            </button>
           )}</div></div>

      {isExpanded && (
        <div className="user-content">
          {/* Services Table - For specific freelancer */}
          <ServicesTable
            tableRows={tableRowsByFreelancer[friendUserID.toString()] || []}
            serviceOptions={serviceOptions}
            currency={currency}
            isHourlyPrice={isHourlyPrice}
            isDailyPrice={isDailyPrice}
            isWeeklyPrice={isWeeklyPrice}
            isMonthlyPrice={isMonthlyPrice}
            deductionType={deductionType}
            t={t}
            onRowChange={(rowId, field, value) => handleRowChange(friendUserID.toString(), rowId, field, value)}
            onRemoveRow={(rowId) => handleRemoveRow(friendUserID.toString(), rowId)}
            onAddRow={(rowData) => handleAddWorkingHoursSection(friendUserID.toString(), rowData)}
            globalData={globalData}
            preferences={preferences}
            onServiceTableDataChange={handleServiceTableDataChange}
            validateServiceDescriptions={validateServiceDescriptionsRef}
            freelancerId={friendUserID.toString()}
            timesheetData={timesheetData}
            customerState={customerState}
          />

          {/* Add Deductions Button */}
          <div style={{borderTop:"1px solid #E9EAEB", borderBottom:"1px solid #E9EAEB", width:"100%",}}>
          <div className="add-deductions-section">
            <Button variant="primary" className="add-deductions-btn" onClick={() => handleOpenDeductionModal(friendUserID.toString())}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3.33333V12.6667M3.33333 8H12.6667" stroke="#252525" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('ADD_DEDUCTIONS')}
            </Button>
          </div>

          {/* Allowances Table */}
          {(() => {
            const workingHoursData = copiedInvoiceData?.workingHours?.filter((wh: any, index: number) => {
              const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
              const isThisFreelancer = wh.userId?.toString() === friendUserID.toString();
              return !isServiceArticle && isThisFreelancer && !removedCopiedWorkingHours.has(index);
            }) || [];
            
            const deductionsData = deductionsByFreelancer[friendUserID.toString()] || [];
            const hasData = workingHoursData.length > 0 || deductionsData.length > 0;
            
            if (!hasData) return null;
            
            return (
              <div className="allowances-table">
                <div className="table-header d-md-grid">
                  <div className="col-drag"></div>
                  <div className="col-dates-allowance">{t('DATE_S')}</div>
                  <div className="col-allowance-type">{t('ALLOWANCE_TYPE')}</div>
                  <div className="col-attachment">{t('ATTACHMENT')}</div>
                  <div className="col-price-allowance">{t('PRICE')}</div>
                  <div className="col-actions-allowance"></div>
                </div>

                <div className="table-body">
                  {/* Working hours from copied invoice data */}
                  {copiedInvoiceData?.workingHours?.filter((wh: any, index: number) => {
                    const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
                    const isThisFreelancer = wh.userId?.toString() === friendUserID.toString();
                    return !isServiceArticle && isThisFreelancer && !removedCopiedWorkingHours.has(index);
                  }).map((workingHour: any) => {
                    const originalIndex = copiedInvoiceData?.workingHours?.findIndex((wh: any) => wh === workingHour) || 0;
                    
                    return (
                      <div className="table-row" key={`wh-${originalIndex}`}>
                        <div className="col-drag d-none d-md-flex">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <circle cx="9" cy="12" r="1" fill="#a4a7ae" />
                            <circle cx="9" cy="5" r="1" fill="#a4a7ae" />
                            <circle cx="9" cy="19" r="1" fill="#a4a7ae" />
                            <circle cx="15" cy="12" r="1" fill="#a4a7ae" />
                            <circle cx="15" cy="5" r="1" fill="#a4a7ae" />
                            <circle cx="15" cy="19" r="1" fill="#a4a7ae" />
                          </svg>
                        </div>

                        <div className="col-dates-allowance" data-label={t('DATE_S')}>
                          <span className="date-value">{formatDate(workingHour.workingDate)}</span>
                        </div>

                        <div className="col-allowance-type" data-label={t('ALLOWANCE_TYPE')}>
                          <div className="input-box">
                            <span className="input-value">{workingHour.houseworkType || workingHour.workingDescription || `Article ${workingHour.articleNumber}`}</span>
                          </div>
                        </div>

                        <div className="col-attachment" data-label={t('ATTACHMENT')}>
                          <div className="attachment-box">
                            <span className="file-name">{workingHour.fileName || t('No file')}</span>
                          </div>
                        </div>

                        <div className="col-price-allowance" data-label={t('PRICE')}>
                          <div className="input-box">
                            <span className="input-value">{workingHour.amount || 0}</span>
                            <span className="currency-symbol">{currency}</span>
                          </div>
                        </div>

                        <div className="col-actions-allowance" data-label={t('ACTIONS')}>
                          <Button 
                            variant="link" 
                            className="edit-btn"
                            onClick={() => { 
                              const deductionData: any = {
                                articleNumber: workingHour.articleNumber,
                                deductionType: '',
                                files: [],
                                fileContent: workingHour.fileContent || '',
                                fileName: workingHour.fileName || ''
                              };

                              // Always populate common fields
                              deductionData.isTaxFree = workingHour.isTaxFreeArticle ? 'yes' : 'no';
                              deductionData.showOnInvoice = workingHour.isArticleShownInInvoice ? 'yes' : 'no';
                              deductionData.showOnInvoiceFile = workingHour.isPdfAttachedToInvoice ? 'yes' : 'no';
                              
                              // IMPORTANT: Always populate VAT from backend for ALL deduction types
                             
                              
                              if (workingHour.vat !== undefined && workingHour.vat !== null) {
                                deductionData.vat = workingHour.vat?.toString() || '';
                              }
                              
                              // Mileage Report fields
                              if (workingHour.distance || workingHour.tripStartedPlace || workingHour.carRegistrationNo) {
                                deductionData.travelDate = workingHour.workingDate || '';
                                deductionData.distance = workingHour.distance?.toString() || workingHour.number?.toString() || '';
                                deductionData.reason = workingHour.reasonForTrip || workingHour.workingDescription || '';
                                deductionData.carRegistration = workingHour.carRegistrationNo || '';
                                deductionData.tripStartPlace = workingHour.tripStartedPlace || '';
                                deductionData.tripEndPlace = workingHour.tripEndedPlace || '';
                                deductionData.total = workingHour.amount?.toString() || workingHour.amountInSek?.toString() || '0';
                              }
                              
                              // Travel/Material expenses with amount fields
                              if (workingHour.amountPerUnit || workingHour.amountExVat) {
                                deductionData.amountExVat = workingHour.amount?.toString() || workingHour.amountInSek?.toString() || '';
                                deductionData.travelDate = workingHour.workingDate || '';
                                deductionData.dateOfPurchase = workingHour.workingDate || '';
                              }
                              
                              // Allowance fields
                              if (workingHour.tripStartedDate || workingHour.tripEndedDate || workingHour.allowancePerDay) {
                                deductionData.tripStartDate = workingHour.tripStartedDate || workingHour.workingDate || '';
                                deductionData.tripEndDate = workingHour.tripEndedDate || workingHour.workingDate || '';
                                deductionData.reason = workingHour.reasonForTrip || workingHour.workingDescription || '';
                                deductionData.country = workingHour.country || '';
                                deductionData.destination = workingHour.allowanceDestination || '';
                                deductionData.allowancePerDay = workingHour.allowancePerDay?.toString() || '';
                                deductionData.isPartialWorkday = '';
                              }

                              setEditingDeductionData(deductionData);
                              setCurrentDeductionFreelancerId(friendUserID.toString());
                              setEditingDeductionIndex(null);
                              setEditingCopiedWorkingHourIndex(originalIndex);
                              setShowDeductionModal(true);
                            }}
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="#317AF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Button>
                          <Button 
                            variant="link" 
                            className="remove-item-btn"
                            onClick={() => {
                              setRemovedCopiedWorkingHours(prev => new Set([...prev, originalIndex]));
                            }}
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M18 6L6 18M6 6l12 12" stroke="#E33B32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Deductions for this freelancer */}
                  {(deductionsByFreelancer[friendUserID.toString()] || []).map((deduction: any, index: number) => {
                    const getDateDisplay = () => {
                      if (deduction.tripStartDate && deduction.tripEndDate) {
                        return `${formatDate(deduction.tripStartDate)} - ${formatDate(deduction.tripEndDate)}`;
                      }
                      return formatDate(deduction.travelDate || deduction.dateOfPurchase || new Date().toISOString());
                    };

                    const getAmount = () => {
                      if (deduction.amountExVat) {
                        const vat = parseFloat(deduction.vat || 0);
                        const amount = parseFloat(deduction.amountExVat);
                        return (amount + (amount * vat / 100)).toFixed(2);
                      }
                      if (deduction.total) return deduction.total;
                      if (deduction.allowancePerDay) return deduction.allowancePerDay;
                      return '-';
                    };

                    return (
                      <div className="table-row" key={index}>
                        <div className="col-drag d-none d-md-flex">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <circle cx="9" cy="12" r="1" fill="#a4a7ae" />
                            <circle cx="9" cy="5" r="1" fill="#a4a7ae" />
                            <circle cx="9" cy="19" r="1" fill="#a4a7ae" />
                            <circle cx="15" cy="12" r="1" fill="#a4a7ae" />
                            <circle cx="15" cy="5" r="1" fill="#a4a7ae" />
                            <circle cx="15" cy="19" r="1" fill="#a4a7ae" />
                          </svg>
                        </div>

                        <div className="col-dates-allowance" data-label={t('DATE_S')}>
                          <span className="date-value">{getDateDisplay()}</span>
                        </div>

                        <div className="col-allowance-type" data-label={t('ALLOWANCE_TYPE')}>
                          <div className="input-box">
                            <span className="input-value">{getDeductionLabel(deduction.deductionType)}</span>
                          </div>
                        </div>

                        <div className="col-attachment" data-label={t('ATTACHMENT')}>
                          <div className="attachment-box">
                            <span className="file-name">
                              {deduction.files && deduction.files.length > 0 ? deduction.files[0].name : t('NO_FILE')}
                            </span>
                            {deduction.files && deduction.files.length > 0 && (
                              <Button variant="link" className="download-btn">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5M5.83333 8.33333L10 12.5M10 12.5L14.1667 8.33333M10 12.5V2.5" stroke="#252525" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="col-price-allowance" data-label={t('PRICE')}>
                          <div className="input-box">
                            <span className="input-value">{getAmount()}</span>
                            <span className="currency-symbol">{currency}</span>
                          </div>
                        </div>

                        <div className="col-actions-allowance" data-label={t('ACTIONS')}>
                          <Button variant="link" className="edit-btn" onClick={() => handleOpenDeductionModal(friendUserID.toString(), index)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
  <path d="M3.21424 16.4937L4.16293 16.81V16.81L3.21424 16.4937ZM4.06269 13.9484L3.11401 13.6322L3.11401 13.6322L4.06269 13.9484ZM4.93817 12.5318L5.64527 13.239L5.64527 13.239L4.93817 12.5318ZM13.3932 4.0768L12.6861 3.36969V3.36969L13.3932 4.0768ZM17.9232 8.60679L18.6303 9.3139V9.3139L17.9232 8.60679ZM9.46815 17.0618L8.76105 16.3547L8.76105 16.3547L9.46815 17.0618ZM8.05161 17.9373L8.36784 18.886H8.36784L8.05161 17.9373ZM5.50626 18.7858L5.82248 19.7344H5.82248L5.50626 18.7858ZM9.00378 17.4953L8.40154 16.697L8.40154 16.697L9.00378 17.4953ZM8.64697 17.7158L8.20226 16.8201L8.64697 17.7158ZM19.0145 5.3361L18.1185 5.78012L18.1185 5.78012L19.0145 5.3361ZM19.0145 7.34749L18.1185 6.90347L18.1185 6.90348L19.0145 7.34749ZM14.6525 2.98552L15.0965 3.88154L15.0965 3.88154L14.6525 2.98552ZM16.6639 2.98552L16.2199 3.88154V3.88154L16.6639 2.98552ZM4.2842 13.353L5.17988 13.7977L5.17988 13.7977L4.2842 13.353ZM4.50473 12.9962L3.70641 12.394L3.70641 12.394L4.50473 12.9962ZM2.79217 18.5215L1.85026 18.8574L2.79217 18.5215ZM3.47849 19.2078L3.14261 20.1497H3.14261L3.47849 19.2078ZM12.9678 4.50223C12.5773 4.11171 11.9441 4.11171 11.5536 4.50223C11.1631 4.89276 11.1631 5.52592 11.5536 5.91644L12.2607 5.20934L12.9678 4.50223ZM16.0836 10.4464C16.4741 10.837 17.1073 10.837 17.4978 10.4464C17.8883 10.0559 17.8883 9.42274 17.4978 9.03222L16.7907 9.73933L16.0836 10.4464ZM3.21424 16.4937L4.16293 16.81L5.01138 14.2646L4.06269 13.9484L3.11401 13.6322L2.26556 16.1775L3.21424 16.4937ZM4.93817 12.5318L5.64527 13.239L14.1003 4.78391L13.3932 4.0768L12.6861 3.36969L4.23106 11.8247L4.93817 12.5318ZM17.9232 8.60679L17.2161 7.89969L8.76105 16.3547L9.46815 17.0618L10.1753 17.7689L18.6303 9.3139L17.9232 8.60679ZM8.05161 17.9373L7.73538 16.9886L5.19003 17.8371L5.50626 18.7858L5.82248 19.7344L8.36784 18.886L8.05161 17.9373ZM9.46815 17.0618L8.76105 16.3547C8.51656 16.5992 8.45917 16.6535 8.40154 16.697L9.00378 17.4953L9.60602 18.2936C9.80031 18.147 9.96852 17.9757 10.1753 17.7689L9.46815 17.0618ZM8.05161 17.9373L8.36784 18.886C8.64521 18.7935 8.87369 18.7197 9.09167 18.6115L8.64697 17.7158L8.20226 16.8201C8.1376 16.8522 8.0634 16.8793 7.73538 16.9886L8.05161 17.9373ZM9.00378 17.4953L8.40154 16.697C8.33907 16.7441 8.27236 16.7853 8.20226 16.8201L8.64697 17.7158L9.09167 18.6115C9.27258 18.5216 9.44477 18.4152 9.60602 18.2936L9.00378 17.4953ZM17.9232 4.0768L17.2161 4.78391C17.8907 5.4585 18.0447 5.63117 18.1185 5.78012L19.0145 5.3361L19.9105 4.89209C19.6542 4.37491 19.1907 3.9301 18.6303 3.3697L17.9232 4.0768ZM17.9232 8.60679L18.6303 9.3139C19.1907 8.7535 19.6542 8.30868 19.9105 7.79151L19.0145 7.34749L18.1185 6.90348C18.0447 7.05242 17.8907 7.2251 17.2161 7.89969L17.9232 8.60679ZM19.0145 5.3361L18.1185 5.78012C18.2938 6.13404 18.2938 6.54956 18.1185 6.90347L19.0145 7.34749L19.9105 7.79151C20.3632 6.87804 20.3632 5.80556 19.9105 4.89209L19.0145 5.3361ZM13.3932 4.0768L14.1003 4.78391C14.7749 4.10933 14.9476 3.95534 15.0965 3.88154L14.6525 2.98552L14.2085 2.0895C13.6913 2.34578 13.2465 2.8093 12.6861 3.36969L13.3932 4.0768ZM17.9232 4.0768L18.6303 3.3697C18.0699 2.80929 17.6251 2.34578 17.1079 2.0895L16.6639 2.98552L16.2199 3.88154C16.3688 3.95535 16.5415 4.10932 17.2161 4.78391L17.9232 4.0768ZM14.6525 2.98552L15.0965 3.88154C15.4504 3.70615 15.866 3.70615 16.2199 3.88154L16.6639 2.98552L17.1079 2.0895C16.1944 1.63683 15.122 1.63683 14.2085 2.0895L14.6525 2.98552ZM4.06269 13.9484L5.01138 14.2646C5.12072 13.9366 5.14778 13.8624 5.17988 13.7977L4.2842 13.353L3.38853 12.9083C3.2803 13.1263 3.20647 13.3548 3.11401 13.6322L4.06269 13.9484ZM4.93817 12.5318L4.23106 11.8247C4.02432 12.0315 3.85298 12.1997 3.70641 12.394L4.50473 12.9962L5.30304 13.5985C5.34652 13.5408 5.40079 13.4834 5.64527 13.239L4.93817 12.5318ZM4.2842 13.353L5.17988 13.7977C5.21468 13.7276 5.25591 13.6609 5.30304 13.5985L4.50473 12.9962L3.70641 12.394C3.58477 12.5552 3.47835 12.7274 3.38853 12.9083L4.2842 13.353ZM3.21424 16.4937L2.26556 16.1775C2.08744 16.7119 1.93094 17.1782 1.8422 17.5559C1.75694 17.9187 1.68322 18.389 1.85026 18.8574L2.79217 18.5215L3.73407 18.1856C3.76991 18.2861 3.7228 18.2958 3.78917 18.0134C3.85205 17.7458 3.9722 17.3822 4.16293 16.81L3.21424 16.4937ZM5.50626 18.7858L5.19003 17.8371C4.61784 18.0278 4.25425 18.1479 3.98663 18.2108C3.70419 18.2772 3.71386 18.2301 3.81437 18.2659L3.47849 19.2078L3.14261 20.1497C3.61105 20.3168 4.08133 20.2431 4.44414 20.1578C4.82179 20.0691 5.28811 19.9126 5.82248 19.7344L5.50626 18.7858ZM2.79217 18.5215L1.85026 18.8574C2.06526 19.4603 2.5397 19.9347 3.14261 20.1497L3.47849 19.2078L3.81437 18.2659C3.77691 18.2526 3.74743 18.2231 3.73407 18.1856L2.79217 18.5215ZM12.2607 5.20934L11.5536 5.91644L16.0836 10.4464L16.7907 9.73933L17.4978 9.03222L12.9678 4.50223L12.2607 5.20934Z" fill="#317AF7"/>
</svg>
                          </Button>
                          <Button variant="link" className="remove-item-btn" onClick={() => {
                            setDeductionsByFreelancer(prev => ({
                              ...prev,
                              [friendUserID.toString()]: prev[friendUserID.toString()].filter((_, i) => i !== index)
                            }));
                          }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M18 6L6 18M6 6l12 12" stroke="#E33B32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          </div>

          {/* Bottom Section with Checkboxes and Total */}
          <div className="bottom-sections-container">
            <div className="bottom-section-left">
              <PensionHolidaySection
                freelancerId={friendUserID.toString()}
                freelancerName={friendName}
                grossSalary={serviceTableDataByFreelancer[friendUserID.toString()]?.totalAmount || 0}
                isRegularEngagement={isRegularEngagement}
                globalData={globalData}
                t={t}
                pensionData={pensionDataByFreelancer}
                holidayData={holidayDataByFreelancer}
                onPensionChange={setPensionDataByFreelancer}
                onHolidayChange={setHolidayDataByFreelancer}
                calculatedAmount={
                  calculatedSubAmount?.response?.freelancersData?.find(
                    (fd: any) => fd.createdBy?.toString() === friendUserID.toString()
                  )?.grossInSek || 0
                }
              />
            </div>
            <div className="bottom-section-right">
              <SubTotalSection
                subTotal={serviceTableDataByFreelancer[friendUserID.toString()]?.totalAmount || 0}
                pensionPay={pensionDataByFreelancer[friendUserID.toString()]?.pensionAmount || 0}
                holidayPay={holidayDataByFreelancer[friendUserID.toString()]?.holidaySavingsFee || 0}
                grandTotal={
                  (serviceTableDataByFreelancer[friendUserID.toString()]?.totalAmount || 0) +
                  (pensionDataByFreelancer[friendUserID.toString()]?.pensionAmount || 0) +
                  (holidayDataByFreelancer[friendUserID.toString()]?.holidaySavingsFee || 0)
                }
                currency={currency}
                isPensionEnabled={pensionDataByFreelancer[friendUserID.toString()]?.pension || false}
                isHolidayEnabled={holidayDataByFreelancer[friendUserID.toString()]?.holidaySavings || false}
                calculatedSubAmount={calculatedSubAmount}
                language={preferences?.language || 'en'}
                vat={typeof preferences?.vat === 'number' ? preferences.vat : (typeof preferences?.vat === 'string' ? parseInt(preferences.vat) : 25)}
                vatAmount={calculatedSubAmount?.response?.totalCalculation?.vat || 0}
                isCalculateInvoiceLoading={isCalculateInvoiceLoading || false}
                friendUserID={friendUserID.toString()}
                onShowCalculation={handleShowCalculation}
                isCalculationLoading={isCalculationLoading}
                onPensionChange={(value) => {
                  setPensionDataByFreelancer(prev => ({
                    ...prev,
                    [friendUserID.toString()]: {
                      ...prev[friendUserID.toString()],
                      pensionAmount: value
                    }
                  }));
                }}
                onHolidayChange={(value) => {
                  setHolidayDataByFreelancer(prev => ({
                    ...prev,
                    [friendUserID.toString()]: {
                      ...prev[friendUserID.toString()],
                      holidaySavingsFee: value
                    }
                  }));
                }}
                t={t}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Calculation Breakdown Modal */}
      <FreelancerCalculationModal
        isOpen={showCalculationModal}
        onClose={() => setShowCalculationModal(false)}
        calculationData={calculationData}
        freelancerId={friendUserID.toString()}
        currency={currency}
      />
      
      {/* Auto Generate Items Modal */}
      <AutoGenerateItemsModal
        isOpen={showAutoGenerateModal}
        onClose={() => setShowAutoGenerateModal(false)}
        freelancerId={friendUserID.toString()}
        freelancerName={friendName}
        onGenerate={(modalData, selectedDates) => {
          if (onAutoGenerateItems) {
            onAutoGenerateItems(friendUserID.toString(), modalData, selectedDates);
          }
        }}
        isRegularEngagement={isRegularEngagement}
        currency={currency}
        t={t}
        showDatesOptions={showDatesOptions}
        initialData={getModalDataByFreelancer ? getModalDataByFreelancer(friendUserID.toString())?.formData : undefined}
        initialSelectedDates={getModalDataByFreelancer ? getModalDataByFreelancer(friendUserID.toString())?.selectedDates : undefined}
      />

      {/* Remove Freelancer Confirmation Modal */}
      {showRemoveConfirmModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)'
        }}>
          <div className="confirmation-modal" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '590px',
            width: '90%',
           boxShadow: '0px 0px 2px 0px rgba(0,0,0,0.08), 0px 2px 2px 0px rgba(0,0,0,0.12)',
            animation: 'fadeIn 0.2s ease-out',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Icon */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '12px 16px',
              borderBottom: 'none'
            }}>
             <button
                onClick={() => setShowRemoveConfirmModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="#252525" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            {/* Body */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              padding: '24px 16px',
              alignItems: 'center'
            }}>
              {t('REMOVE_FREELANCER') || 'Remove Freelancer'}
             {/* Title */}
              <h3 style={{
                margin: '0',
                fontSize: '20px',
                fontWeight: 700,
                color: '#000000',
                fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                textAlign: 'center',
                lineHeight: '18px'
              }}>
                {t('REMOVE_FREELANCER_TITLE') || 'Remove freelancer from this collective invoice?'}
              </h3>

              {/* Message */}
              <div style={{
                margin: '0',
                fontSize: '16px',
                fontWeight: 400,
                color: '#252B37',
                lineHeight: '1.4',
                fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                textAlign: 'center',
                maxWidth: '476px'
              }}
             dangerouslySetInnerHTML={{
                  __html: t('CONFIRM_REMOVE_FREELANCER_MESSAGE')
                    .replaceAll('<strong>', '')
                    .replaceAll('</strong>', '')
                    .replace('{{name}}', `<strong style="font-weight: 700;">${friendName}</strong>`)
                }}
              />
            </div>
            
            {/* Buttons */}
            <div style={{
              padding: '24px 16px',
              display: 'flex',
              justifyContent: 'center',
              gap: '16px'
            }}>
              <button
                onClick={() => setShowRemoveConfirmModal(false)}
                style={{
                  padding: '10px 18px',
                  fontSize: '14px',
                 fontWeight: 700,
                  borderRadius: '25px',
                  border: '2px solid #6750B8',
                  color: '#544196',
                  backgroundColor: 'transparent',
                  fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  cursor: 'pointer',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  letterSpacing: '0.1px',
                  lineHeight: '20px'
                }}
              >
                {t('CANCEL_BUTTON') || 'Cancel'}
              </button>
              <button
                onClick={() => {
                  if (onRemoveFreelancer) {
                    onRemoveFreelancer(friendUserID.toString());
                  }
                  setShowRemoveConfirmModal(false);
                }}
                style={{
                 padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: 700,
                  borderRadius: '25px',
                  backgroundColor: '#6750B8',
                  border: 'none',
                  color: 'white',
                   fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  cursor: 'pointer',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0px 1px 2px 0px rgba(16,24,40,0.04)',
                  lineHeight: '18px'
                }}
              >
                 {t('REMOVE_FREELANCER_BUTTON') || 'Remove Freelancer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
